import * as Sentry from "@sentry/react";
import { Fragment, Suspense, useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { routes } from "./utils/routes";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { get, handleSessionExpired } from "./utils/api";
import { captureError } from "./utils/errorHandler";
import { fetchFiles } from "./redux/filesSlice";
import { fetchDashboard } from "./redux/dashboardSlice";
import { getOnboardingRoute } from "./utils/onboardingRoutes";
import { fetchSupportedExtensions } from "./utils/supportedExtensions";
import { flushActivity, installActivityFlushHandlers, logActivity } from "./utils/activityLogger";
import { createActiveDurationTracker } from "./utils/activeDurationTracker";
import { authLanded, installAuthDiagnostics } from "./utils/authDiagnostics";
import "react-simple-toasts/dist/style.css";
import "react-simple-toasts/dist/theme/success.css";
import "react-simple-toasts/dist/theme/failure.css";
import "./App.css";

function ActivityRouteTracker({ isAuthenticated }) {
  const location = useLocation();
  const currentViewRef = useRef(null);

  useEffect(() => installAuthDiagnostics(), []);
  useEffect(() => authLanded(location.pathname), [location.pathname]);

  useEffect(() => installActivityFlushHandlers(), []);

  useEffect(() => {
    if (!isAuthenticated) {
      currentViewRef.current = null;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const path = `${location.pathname}${location.search || ""}`;
    const wallStartedAt = Date.now();
    const tracker = createActiveDurationTracker();
    tracker.start(wallStartedAt);
    currentViewRef.current = { path, tracker, wallStartedAt };
    logActivity("page_view_started", { path });

    let hasEnded = false;
    const endCurrentView = () => {
      if (hasEnded) return;
      const activeView = currentViewRef.current;
      if (!activeView || activeView.path !== path) return;
      hasEnded = true;
      logActivity("page_view_ended", {
        path,
        wall_duration_ms: Math.max(0, Date.now() - activeView.wallStartedAt),
      }, {
        durationMs: activeView.tracker.stop(),
        flushNow: true,
        preferBeacon: true,
      });
      flushActivity({ preferBeacon: true });
      currentViewRef.current = null;
    };

    window.addEventListener("pagehide", endCurrentView);

    return () => {
      window.removeEventListener("pagehide", endCurrentView);
      endCurrentView();
    };
  }, [isAuthenticated, location.pathname, location.search]);

  return null;
}

function AskHandoff() {
  useEffect(() => {
    window.location.reload();
  }, []);
  return null;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const dispatch = useDispatch();

  // Pre-fetch files and dashboard data
  const prefetchData = async () => {
    try {
      // Fetch files and dashboard in parallel
      dispatch(fetchFiles());
      dispatch(fetchDashboard());
    } catch (error) {
      captureError(error, "pre-fetching data on app load");
    }
  };

  // Pre-fetch supported file extensions on app load (no auth required, non-blocking)
  useEffect(() => {
    fetchSupportedExtensions();
  }, []);

  useEffect(() => {
    const validateToken = async () => {
      // Pages that run on a pending (pre-verification) MFA session manage
      // their own auth state; validating here would 401 and boot the user
      // out mid-flow.
      const selfManagedAuthPaths = ["/custom-token", "/mfa", "/reset-password"];
      if (selfManagedAuthPaths.includes(window.location.pathname)) {
        setIsAuthenticated(false);
        return;
      }

      const token = localStorage.getItem("token");

      if (!token) {
        setIsAuthenticated(false);
        return;
      }

      try {
        const response = await get("/auth/validate-token/");

        if (response.data?.valid) {
          setIsAuthenticated(true);
          // Update email in localStorage if provided
          if (response.data?.email) {
            localStorage.setItem("userEmail", response.data.email);
            Sentry.setUser({ email: response.data.email });
          }
          // Pre-fetch data after successful token validation
          prefetchData();
        } else {
          // Invalid token - use shared handler to clear data, show toast, and redirect
          setIsAuthenticated(false);
          handleSessionExpired("token validation returned invalid");
        }
      } catch (error) {
        // 401 errors are already handled by the axios interceptor in api.jsx
        // Only handle non-401 errors here
        if (error.response?.status !== 401) {
          captureError(error, "validating auth token");
          setIsAuthenticated(false);
          handleSessionExpired("token validation error");
        } else {
          setIsAuthenticated(false);
        }
      }
    };

    validateToken();
  }, []);

  // Determine where to redirect from root based on localStorage (instant, no API wait)
  const getRootRedirect = () => {
    const token = localStorage.getItem("token");
    
    // Not logged in - go to login
    if (!token) {
      return <Navigate to="/login" replace />;
    }
    
    // Logged in - get route based on onboarding status
    return <Navigate to={getOnboardingRoute()} replace />;
  };

  return (
    <Fragment>
      <BrowserRouter>
        <ActivityRouteTracker isAuthenticated={isAuthenticated} />
        <Routes>
          <Route path="/" element={getRootRedirect()} />
          {/* Ask is served by its own server behind a Vercel rewrite; a client-side navigation here (e.g. the
              post-login redirect) never leaves the SPA, so it becomes a real page load instead. */}
          <Route path="/ask/*" element={<AskHandoff />} />
          {routes.map((route, idx) => {
            const Component = route.component;
            const Layout = route.layout || Fragment;
            const AuthRequire = route.guard || Fragment;
            return (
              <Route
                key={idx}
                path={route.path}
                element={
                  <AuthRequire>
                    <Layout>
                      <Sentry.ErrorBoundary key={route.path} fallback={
                        <div role="alert" className="p-6 text-center">
                          <p>This page couldn’t load. Please refresh to try again.</p>
                          <button className="mt-3 underline" onClick={() => window.location.reload()}>Refresh page</button>
                        </div>
                      }>
                        <Suspense fallback={<p role="status" className="p-6 text-center">Loading…</p>}>
                          {Component}
                        </Suspense>
                      </Sentry.ErrorBoundary>
                    </Layout>
                  </AuthRequire>
                }
              />
            );
          })}
        </Routes>
      </BrowserRouter>
    </Fragment>
  );
}

export default App;
